<template>
    <v-layout class="print-container">
        <!-- large screens navigation drawer -->
        <v-navigation-drawer
            class="navbar no-print d-print-none"
            permanent
            v-if="isAuthenticated && display.mdAndUp"
        >
            <navbar />
        </v-navigation-drawer>

        <v-main>
            <!-- app bar-->
            <v-app-bar
                v-if="isAuthenticated"
                class="appbar no-print d-print-none"
                elevation="0"
                density="compact"
            >
                <!-- trigger for mobile navigation -->
                <icon-button
                    v-if="display.smAndDown"
                    data-cy="mobileMenuButton"
                    btn-style="default-btn medium-icon-btn transparent-btn ml-5"
                    :icon-src="getImageUrl('icons/20x20/ic20-menu.svg')"
                    @run-method="drawer = !drawer"
                />
                <partner-app-bar />
            </v-app-bar>

            <!-- mobile navigation drawer-->
            <v-navigation-drawer
                v-model="drawer"
                location="start"
                temporary
                v-if="isAuthenticated && display.smAndDown"
                class="mobile-navbar no-print"
            >
                <navbar />
            </v-navigation-drawer>
            <!-- page content -->
            <v-container class="main" fluid>
                <router-view />
                <alert
                    v-model="alertConfiguration.visible"
                    :alert-message="alertConfiguration.message"
                    :alert-color="alertConfiguration.color"
                    :alert-parameters="alertConfiguration.alertParameters"
                ></alert>

                <updated-access-pop-up
                    :dialog-visible="showAccessPopUp"
                    @update:dialog-visible="updatePopUpVisible"
                ></updated-access-pop-up>
            </v-container>
        </v-main>
    </v-layout>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useDisplay } from "vuetify";
import { useSessionStore } from "@/stores/session";
import { useAuthenticator } from "@aws-amplify/ui-vue";

import navbar from "@/components/structural/NavBar.vue";
import PartnerAppBar from "@/components/structural/PartnerAppBar.vue";
import iconButton from "@/components/buttons/iconButton.vue";
import alert from "@/components/misc/alert.vue";
import updatedAccessPopUp from "@/components/structural/updatedAccessPopUp.vue";

import { alertConfiguration } from "@/methods/errorHandling";
import { getImageUrl } from "@/methods/iconHelpers";
import { setSentryUser } from "@/methods/sentry";
import { isEmptyOrUndefinedString } from "@/methods/validationRules";
// import { checkUserPermissions } from "@/apis/esendaV2/user"; // TODO: enable when API is ready
import { endInactiveUserSession } from "@/cognito-auth/sessionManagement";

const auth = useAuthenticator();
const sessionStore = useSessionStore();

const isAuthenticated = ref(auth.authStatus === "authenticated");

// breakpoints
const display = ref(useDisplay());
// mobile navigation drawer
const drawer = ref(false);

const showAccessPopUp = ref(false);
const updatePopUpVisible = (value: boolean) => {
    showAccessPopUp.value = value;
};
const getPermissions = async () => {
    const userEmail = sessionStore.userEmail;
    if (userEmail !== null) {
        // TODO: enable when API is ready
        //const updated = await checkUserPermissions(userEmail);
        //showAccessPopUp.value = updated;
    }
};

let intervalId;
const runIntervalPermissionsCheck = () => {
    /** Check permissions every 1 min */
    intervalId = setInterval(() => {
        getPermissions();
    }, 60000);
};

watch(
    auth,
    () => {
        isAuthenticated.value = auth.authStatus === "authenticated";
    },
    { immediate: true },
);

onMounted(() => {
    /** Update the user email in sentry if the auth email is not empty */
    const userEmail = sessionStore.userEmail;
    if (!isEmptyOrUndefinedString(userEmail)) {
        setSentryUser(userEmail as string);
    }
    runIntervalPermissionsCheck();
});

onBeforeUnmount(() => {
    /** Clean up the interval when the component is destroyed */
    if (intervalId) {
        clearInterval(intervalId);
    }
});

/** Log out user after 5 mins of inactivity */
endInactiveUserSession();
</script>
